/**
 * ChunkReloadGuard — graceful recovery bij "Failed to fetch dynamically
 * imported module" na een deploy (#1065).
 *
 * Probleem dat dit oplost:
 *  - Content-gehashte route-chunks (React.lazy + import()) krijgen bij elke
 *    deploy nieuwe namen; de oude verdwijnen van de server.
 *  - Een tab die nog op de oude build draait laadt bij navigatie een chunk die
 *    niet meer bestaat. De SPA-catch-all serveert dan index.html (200 text/html)
 *    i.p.v. JS → de browser werpt een module-load-fout → crash-scherm.
 *
 * Werking:
 *  1. Vite emit een `vite:preloadError` event wanneer een dynamische import
 *     faalt (modulepreload of import()).
 *  2. We herladen dan éénmalig stil de pagina — de server levert dan de nieuwe
 *     index.html + actuele chunk-namen.
 *
 * Loop-protectie: een tijdstempel in sessionStorage. Een tweede fout binnen de
 * cooldown betekent dat de chunk écht kapot is (niet enkel verouderd); dan
 * onderdrukken we de reload en laten de ErrorBoundary het netjes tonen. Ná de
 * cooldown (bv. een latere, échte deploy) mag een reload weer.
 */

/** Cooldown waarbinnen we niet opnieuw herladen — voorkomt een reload-storm. */
export const CHUNK_RELOAD_COOLDOWN_MS = 10_000;

/** Zo lang na beforeunload/pagehide geldt de pagina als vertrekkend (#1847). */
export const VERTREK_MS = 3_000;

/** sessionStorage-sleutel met de timestamp van de laatste auto-reload. */
const RELOAD_TS_KEY = 'pt_chunk_reload_ts';

export type ChunkReloadResult = 'reloaded' | 'suppressed-cooldown';

/** Minimale storage-interface zodat tests een fake kunnen injecteren. */
interface MinimalStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface ChunkReloadOptions {
  /** Override reload — alleen voor tests. */
  reload?: () => void;
  /** Override storage — alleen voor tests. */
  storage?: MinimalStorage;
  /** Override de klok — alleen voor tests. */
  now?: () => number;
}

/**
 * Bepaal of we mogen herladen en doe dat zo nodig. Idempotent binnen de
 * cooldown dankzij de timestamp-guard.
 */
export const handleChunkLoadError = (opts: ChunkReloadOptions = {}): ChunkReloadResult => {
  const reload = opts.reload ?? (() => window.location.reload());
  const storage = opts.storage ?? sessionStorage;
  const now = opts.now ?? (() => Date.now());

  const raw = storage.getItem(RELOAD_TS_KEY);
  const last = raw != null ? Number(raw) : NaN;
  const current = now();

  // Geldige eerdere reload binnen de cooldown → onderdruk (chunk echt kapot).
  if (Number.isFinite(last) && current - last < CHUNK_RELOAD_COOLDOWN_MS) {
    return 'suppressed-cooldown';
  }

  // Eerste fout, corrupte guard, of cooldown verstreken → herlaad éénmalig.
  storage.setItem(RELOAD_TS_KEY, String(current));
  reload();
  return 'reloaded';
};

/** Detecteer of een willekeurige error een dynamische-import-fout is. */
export const isChunkLoadError = (reason: unknown): boolean => {
  const msg = reason instanceof Error ? reason.message : typeof reason === 'string' ? reason : '';
  return (
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg)
  );
};

/**
 * Registreer de globale listeners. Aanroepen vanuit index.tsx bij app-start.
 *
 * - `vite:preloadError` — primaire bron; Vite firet dit bij een gefaalde
 *   dynamische import. We voorkomen de default (die anders de fout opnieuw
 *   throwt) en herladen via de guard.
 * - `unhandledrejection` — vangnet voor chunk-fouten die niet als
 *   preloadError langskomen (bv. directe import()-rejectie).
 *
 * Geeft een cleanup-functie terug (handig voor tests / HMR).
 */
export const installChunkReloadGuard = (opts: ChunkReloadOptions = {}): (() => void) => {
  // #1847 — WebKit/Safari laat lopende import()'s falen ("Importing a module script failed.")
  // zodra een navigatie begint. Herladen zou dan de óude pagina terughalen en de navigatie
  // afbreken. Tijdens het weggaan doen we dus niets. Een afgebroken vertrek (beforeunload
  // zonder navigatie) zet de guard na VERTREK_MS weer aan.
  let vertrekt = false;
  let vertrekTimer: ReturnType<typeof setTimeout> | undefined;
  const onVertrek = () => {
    vertrekt = true;
    clearTimeout(vertrekTimer);
    vertrekTimer = setTimeout(() => (vertrekt = false), VERTREK_MS);
  };

  const onPreloadError = (event: Event) => {
    // Voorkom dat Vite de fout opnieuw naar window.onerror gooit.
    event.preventDefault();
    if (!vertrekt) handleChunkLoadError(opts);
  };

  const onUnhandledRejection = (event: PromiseRejectionEvent) => {
    if (isChunkLoadError(event.reason)) {
      event.preventDefault();
      if (!vertrekt) handleChunkLoadError(opts);
    }
  };

  window.addEventListener('vite:preloadError', onPreloadError as EventListener);
  window.addEventListener('unhandledrejection', onUnhandledRejection);
  window.addEventListener('beforeunload', onVertrek);
  window.addEventListener('pagehide', onVertrek);

  return () => {
    clearTimeout(vertrekTimer);
    window.removeEventListener('vite:preloadError', onPreloadError as EventListener);
    window.removeEventListener('unhandledrejection', onUnhandledRejection);
    window.removeEventListener('beforeunload', onVertrek);
    window.removeEventListener('pagehide', onVertrek);
  };
};
